import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Grid,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  Save,
  Send,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  Visibility
} from '@mui/icons-material';
import api from '../services/api';
import placesService from '../services/placesService';
import { parseVerificationPhotos, sanitizePhotosForSubmit } from '../utils/verificationDraft';
import SimpleVerificationReport from '../components/verifiers/SimpleVerificationReport';
import DialogCloseButton from '../components/common/DialogCloseButton';

// Import AI components
import GeofenceVerification from '../components/verifier/GeofenceVerification';
import AIPhotoAnalyzer from '../components/verifier/AIPhotoAnalyzer';
import AIInspectionReportField from '../components/verifier/AIInspectionReportField';
import AIComplianceChecker from '../components/verifier/AIComplianceChecker';
import ListingFindingsStep, { isListingFindingsComplete, forTenure } from '../components/verification/ListingFindingsStep';
import { nextVisibleStep, prevVisibleStep } from '../components/verification/stepNavigation';

/**
 * Structured Verification Workflow
 * Clear 6-step process for completing verifications
 * Replaces confusing conversational AI with predictable workflow
 */
const LISTING_TEMPLATE_BLOCKED = 'The listing findings form could not be loaded — retry before continuing';

const StructuredVerificationWorkflow = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // Loading states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Verification data
  const [verification, setVerification] = useState(null);
  const [verifierInfo, setVerifierInfo] = useState(null);

  // Workflow state
  const [activeStep, setActiveStep] = useState(0);
  const [locationVerified, setLocationVerified] = useState(false);
  const [locationData, setLocationData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [reportContent, setReportContent] = useState('');
  const [complianceResult, setComplianceResult] = useState(null);
  const [siteCoordinates, setSiteCoordinates] = useState(null);
  const [initialPhotos, setInitialPhotos] = useState([]);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [showClientPreview, setShowClientPreview] = useState(false);
  const skipInitialSaveRef = useRef(true);

  // Listing verifications (a realtor listing check) carry an extra structured
  // findings step whose fields come from the package's scope template.
  const [allListingFields, setListingFields] = useState([]);
  // Title-deed listings get the Deeds Office questions; cession listings get the
  // council/developer register questions instead (fields tagged with `tenure`).
  const listingFields = forTenure(allListingFields, verification?.tenureType);
  const [listingFindings, setListingFindings] = useState({});
  const [listingTemplateError, setListingTemplateError] = useState(false);
  const [showFindingErrors, setShowFindingErrors] = useState(false);
  const isListingVerification = !!verification?.listingId;

  // Steps are addressed by NAME everywhere below — inserting 'Listing findings'
  // shifts every later index, so index-based step logic would silently break.
  const steps = useMemo(() => [
    'Verify Location',
    'Document with Photos',
    'Write Report',
    ...(isListingVerification ? ['Listing findings'] : []),
    'Compliance Check',
    'Review & Submit'
  ], [isListingVerification]);

  // Moving between steps (which skips the Professional-only compliance step)
  // lives in stepNavigation.js — pure, name-based, and unit-tested.
  const verifierTier = verifierInfo?.tier;

  // Load verification data
  useEffect(() => {
    loadVerificationData();
  }, [id]);

  // Auto-save the draft (debounced) whenever photos, report, or step change so
  // progress survives the verifier leaving the site / closing the app.
  useEffect(() => {
    if (loading || !verification) return;
    // Never race the submission: submit rewrites the findings server-side and a
    // late autosave must not land on top of (or be refused by) that write.
    if (submitting) return;
    // The first effect run after data loads is just the rehydrated state
    // settling — don't write it straight back (avoids a no-op PATCH and a
    // misleading "Saved ✓" the instant the page opens).
    if (skipInitialSaveRef.current) {
      skipInitialSaveRef.current = false;
      return;
    }
    const handle = setTimeout(() => {
      saveDraft();
    }, 1500);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos, reportContent, listingFindings, activeStep, loading, verification, submitting]);

  // Skip the Professional-only compliance step for General-tier verifiers.
  // Done in an effect (never during render) so we don't setState while
  // rendering — also covers resuming directly onto the compliance step.
  useEffect(() => {
    if (steps[activeStep] === 'Compliance Check' && verifierTier && verifierTier !== 'professional') {
      setActiveStep(nextVisibleStep(steps, verifierTier, activeStep));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, verifierTier, steps]);

  const saveDraft = async () => {
    try {
      setDraftSaving(true);
      const res = await api.patch(`/simple-verifications/${id}/draft`, {
        photos: sanitizePhotosForSubmit(photos),
        reportContent,
        draftStep: activeStep,
        ...(isListingVerification ? { listingFindings } : {}),
      });
      if (res.data?.success) {
        setDraftSavedAt(new Date());
      }
    } catch (err) {
      // Non-blocking: a failed autosave is retried on the next change.
      console.warn('Draft autosave failed:', err?.message);
    } finally {
      setDraftSaving(false);
    }
  };

  // The findings form is driven by the package's scope template. If it can't be
  // fetched the verifier must not be allowed to submit a blank findings set —
  // we surface the failure and offer a retry instead of silently continuing.
  const loadListingTemplate = async (packageSlug) => {
    try {
      const slug = packageSlug || 'listing_verification';
      const tpl = await api.get(`/property-checks/scope-template/${slug}`);
      const fields = tpl.data?.data?.template?.listingFields || [];
      setListingFields(fields);
      setListingTemplateError(fields.length === 0);
    } catch (tplErr) {
      console.warn('Could not load listing findings template:', tplErr?.message);
      setListingFields([]);
      setListingTemplateError(true);
    }
  };

  const loadVerificationData = async () => {
    try {
      setLoading(true);

      // Load verification details
      const verificationResponse = await api.get(`/simple-verifications/${id}`);
      const vData = verificationResponse.data.success ? verificationResponse.data.data : null;

      if (vData) {
        setVerification(vData);

        // If the verifier already checked in on-site (POST /start persisted the
        // proof), don't re-gate them on resume — they may now be off-site
        // finishing the report. Treat a stored check-in as location-verified.
        try {
          const storedGps = typeof vData.gpsLocation === 'string'
            ? JSON.parse(vData.gpsLocation || 'null')
            : vData.gpsLocation;
          if (storedGps && (storedGps.checkedInAt || storedGps.checkInGeofence)) {
            checkInSavedRef.current = true;
            setLocationVerified(true);
            setLocationData({ verified: true, position: storedGps, message: 'On-site check-in recorded' });
          }
        } catch (_) { /* ignore malformed gpsLocation */ }

        // Resume the draft: seed previously-saved photos, report, and step so
        // the verifier continues where they left off instead of restarting.
        const seededPhotos = parseVerificationPhotos(vData);
        if (seededPhotos.length > 0) {
          setInitialPhotos(seededPhotos);
          setPhotos(seededPhotos);
        }
        if (vData.aiReportContent) {
          setReportContent(vData.aiReportContent);
        }
        if (Number.isInteger(vData.draftStep) && vData.draftStep > 0) {
          // A draft saved against a different step list must never point past
          // the end of the steps we're about to render. The listing-findings
          // step is the only optional one, so the length is known here.
          const stepCount = vData.listingId ? 6 : 5;
          setActiveStep(Math.min(vData.draftStep, stepCount - 1));
        }
        if (vData.draftSavedAt) {
          setDraftSavedAt(new Date(vData.draftSavedAt));
        }

        // Listing checks: load the package's findings form and rehydrate any
        // answers already saved with the draft.
        if (vData.listingId) {
          if (vData.listingFindings && typeof vData.listingFindings === 'object') {
            setListingFindings(vData.listingFindings);
          }
          await loadListingTemplate(vData.packageSlug);
        }
      }

      // Load verifier profile
      try {
        const verifierResponse = await api.get('/verifiers/profile/me');
        if (verifierResponse.data.success) {
          setVerifierInfo(verifierResponse.data.data);
        }
      } catch (err) {
        console.error('Error loading verifier profile:', err);
      }

      setError(null);

      // Resolve site coordinates for geofence verification
      const v = verificationResponse.data.data;

      // 1. Try gpsLocation (populated if verification was already started)
      const gps = typeof v.gpsLocation === 'string' ? JSON.parse(v.gpsLocation || 'null') : v.gpsLocation;
      if (gps?.lat && gps?.lng) {
        setSiteCoordinates({ latitude: gps.lat, longitude: gps.lng, address: v.projectLocation || 'Site location' });
      }
      // 2. Try geocoding the project's address
      else if (v.projectLocation) {
        try {
          const geo = await placesService.geocodeAddress(v.projectLocation);
          if (geo.success && geo.data?.coordinates) {
            setSiteCoordinates({
              latitude: geo.data.coordinates.lat,
              longitude: geo.data.coordinates.lng,
              address: v.projectLocation
            });
          } else {
            // Geocoding returned no results — use GPS-only mode
            setSiteCoordinates(null);
          }
        } catch (geoErr) {
          console.warn('Could not geocode project location:', geoErr);
        }
      }
    } catch (err) {
      console.error('Error loading verification:', err);
      setError('Failed to load verification details');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    // Validate current step before proceeding (by step NAME — the listing step
    // shifts the indices of every step after 'Write Report').
    const current = steps[activeStep];
    if (current === 'Verify Location' && !locationVerified) {
      alert('Please verify your location before proceeding');
      return;
    }
    if (current === 'Document with Photos' && photos.length === 0) {
      alert('Please add at least one site photo before continuing. Photos help clients trust the report and protect your work.');
      return;
    }
    if (current === 'Write Report' && !reportContent.trim()) {
      alert('Please write your inspection report before proceeding');
      return;
    }
    if (current === 'Listing findings') {
      if (listingFields.length === 0) {
        alert(LISTING_TEMPLATE_BLOCKED);
        return;
      }
      if (!isListingFindingsComplete(listingFields, listingFindings)) {
        setShowFindingErrors(true);
        alert('Answer every required listing finding first');
        return;
      }
    }
    // General-tier verifiers skip the Professional-only compliance step.
    setActiveStep((prevStep) => nextVisibleStep(steps, verifierTier, prevStep));
  };

  const handleBack = () => {
    // Mirror the forward skip: General-tier verifiers jump back over compliance.
    setActiveStep((prevStep) => prevVisibleStep(steps, verifierTier, prevStep));
  };

  // Persist the on-site check-in to the backend the first time the verifier
  // proves they are at the site. This is the single geofence gate: once checked
  // in, they can leave and finish/edit/submit the report from anywhere. Guard
  // with a ref so re-renders/re-verifies don't re-POST.
  const checkInSavedRef = useRef(false);

  const handleLocationVerification = async (result) => {
    setLocationVerified(result.verified);
    setLocationData(result);
    if (!result.verified || checkInSavedRef.current) return;
    checkInSavedRef.current = true;
    try {
      await api.post(`/simple-verifications/${id}/start`, {
        gpsLocation: result.position || result,
      });
    } catch (err) {
      // The backend re-checks the geofence at check-in. If it rejects, surface
      // it and let the verifier retry from on-site.
      checkInSavedRef.current = false;
      const msg = err?.response?.data?.message
        || 'Could not record your on-site check-in. Please try again from the site.';
      if (err?.response?.data?.code === 'CHECK_IN_GEOFENCE_FAILED') {
        setLocationVerified(false);
      }
      alert(msg);
    }
  };

  const handlePhotosChanged = (photoData) => {
    if (Array.isArray(photoData)) {
      setPhotos(photoData);
    }
  };

  const handleComplianceChecked = (result) => {
    setComplianceResult(result);
  };

  const handleSubmit = async () => {
    if (!locationVerified) {
      alert('Please complete the on-site check-in (Step 1) before submitting. Once checked in, you can finish the report from anywhere.');
      return;
    }

    if (!reportContent || reportContent.trim().length === 0) {
      alert('Report content is required');
      return;
    }

    if (photos.length === 0) {
      alert('At least one site photo is required before submitting a verifier report');
      return;
    }

    if (isListingVerification && listingFields.length === 0) {
      alert(LISTING_TEMPLATE_BLOCKED);
      setActiveStep(steps.indexOf('Listing findings'));
      return;
    }

    if (isListingVerification && !isListingFindingsComplete(listingFields, listingFindings)) {
      setShowFindingErrors(true);
      alert('Answer every required listing finding first');
      setActiveStep(steps.indexOf('Listing findings'));
      return;
    }

    setSubmitting(true);
    try {
      // Prepare submission data
      const reportData = {
        reportContent,
        locationData,
        ...(isListingVerification ? { listingFindings } : {}),
        // Only submit photos backed by a durable URL. A blob: preview URL can't
        // be fetched server-side, so it must never reach the report/PDF pipeline.
        photos: sanitizePhotosForSubmit(photos),
        complianceResult,
        status: 'completed',
        completedAt: new Date().toISOString()
      };

      // Submit to backend
      const response = await api.post(`/simple-verifications/${id}/submit`, reportData);

      if (response.data.success) {
        alert('Verification submitted successfully!');
        // Land on the final report (the same view the client receives) so the
        // verifier immediately sees what was delivered.
        navigate(`/verifier/simple-verifications/${id}/report`);
      } else {
        throw new Error(response.data.error || 'Submission failed');
      }
    } catch (err) {
      console.error('Error submitting verification:', err);
      const data = err?.response?.data;
      if (data?.errorCode === 'INVALID_FINDINGS') {
        // The server re-validates the findings against the package template.
        const label = (fid) => listingFields.find((f) => f.id === fid)?.label || fid;
        const problems = [...(data.missing || []), ...(data.invalid || [])].map(label);
        setShowFindingErrors(true);
        alert(`Some listing findings still need attention:\n\n${problems.join('\n')}`);
        const idx = steps.indexOf('Listing findings');
        if (idx >= 0) setActiveStep(idx);
      } else if (data?.errorCode === 'LISTING_NOT_AWAITING_VERIFICATION') {
        alert(data.message || 'This listing has already been ruled on; findings can no longer be submitted.');
      } else {
        alert(`Failed to submit verification: ${data?.message || err.message}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Mirrors the backend's buildPreparedReportSummary (simpleVerifications.js)
  // so the previewed AI Summary matches what the client will actually receive.
  const buildPreviewSummary = (content) => {
    const plain = String(content || '')
      .replace(/^#+\s+/gm, '')
      .replace(/\*\*/g, '')
      .trim();
    if (!plain) return 'Verification report submitted for client review.';
    const firstParagraph = plain.split(/\n\s*\n/).find(Boolean) || plain;
    return firstParagraph.length > 280 ? `${firstParagraph.slice(0, 277)}...` : firstParagraph;
  };

  // Assemble the verification object the client's report page would receive,
  // from local (unsubmitted) workflow state, for the client-view preview.
  const buildPreviewVerification = () => ({
    id,
    title: verification?.title,
    status: 'completed',
    verificationType: verification?.verificationType,
    projectName: verification?.projectName,
    aiReportContent: reportContent,
    aiSummary: buildPreviewSummary(reportContent),
    aiRiskLevel: 'low',
    aiConfidenceScore: 1,
    aiRecommendations: [],
    checklistResults: [],
    photos: sanitizePhotosForSubmit(photos),
    verifierNotes: '',
    verificationDuration: null
  });

  // Aggregate every photo's AI analysis into a single context object so the
  // report generator reasons over ALL findings together, not photo-by-photo.
  const buildPhotoAnalysisContext = () => {
    const analyzed = photos.filter(p => p.analysis && typeof p.analysis === 'object');

    const featureSet = new Set();
    const issues = [];
    const materialsSet = new Set();
    const siteConditions = [];
    const conditionNotes = [];
    let qualitySum = 0, qualityCount = 0;
    let safetySum = 0, safetyCount = 0;
    let compliantCount = 0, nonCompliantCount = 0;

    const perPhoto = analyzed.map((p, idx) => {
      const a = p.analysis;
      (a.detectedFeatures || []).forEach(f => featureSet.add(f));
      (a.materials || []).forEach(m => materialsSet.add(m));
      if (Array.isArray(a.issues)) issues.push(...a.issues);
      if (a.siteConditions) siteConditions.push(a.siteConditions);
      if (a.conditionAssessment) conditionNotes.push(a.conditionAssessment);
      if (typeof a.qualityScore === 'number') { qualitySum += a.qualityScore; qualityCount++; }
      if (typeof a.safetyScore === 'number') { safetySum += a.safetyScore; safetyCount++; }
      if (a.complianceStatus === 'COMPLIANT') compliantCount++;
      else if (a.complianceStatus === 'NON_COMPLIANT') nonCompliantCount++;

      return {
        photo: p.name || `Photo ${idx + 1}`,
        detectedFeatures: a.detectedFeatures || [],
        issues: a.issues || [],
        qualityScore: a.qualityScore ?? null,
        safetyScore: a.safetyScore ?? null,
        complianceStatus: a.complianceStatus ?? null,
        conditionAssessment: a.conditionAssessment ?? null
      };
    });

    return {
      photoCount: photos.length,
      analyzedCount: analyzed.length,
      aggregate: {
        detectedFeatures: Array.from(featureSet),
        materials: Array.from(materialsSet),
        allIssues: [...new Set(issues)],
        totalIssues: issues.length,
        avgQualityScore: qualityCount ? +(qualitySum / qualityCount).toFixed(1) : null,
        avgSafetyScore: safetyCount ? +(safetySum / safetyCount).toFixed(1) : null,
        compliantPhotos: compliantCount,
        nonCompliantPhotos: nonCompliantCount,
        siteConditions,
        conditionNotes
      },
      perPhoto
    };
  };

  const renderStepContent = (stepIndex) => {
    // Switch on the step NAME, not its index: the listing-findings step is only
    // present for listing verifications and shifts every later index.
    const stepNumber = stepIndex + 1;
    switch (steps[stepIndex]) {
      case 'Verify Location':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step {stepNumber}: Verify Your Location
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You must be physically present at the inspection site to check in. Once checked in, you can leave and finish or edit your report from anywhere.
            </Typography>
            {siteCoordinates ? (
              <GeofenceVerification
                siteAddress={siteCoordinates}
                allowedRadius={100}
                onVerificationChange={handleLocationVerification}
              />
            ) : !loading ? (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Site coordinates could not be determined from the project address.
                  Your GPS location will be recorded when you verify.
                </Alert>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={async () => {
                    try {
                      const pos = await new Promise((resolve, reject) =>
                        navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 })
                      );
                      setLocationVerified(true);
                      setLocationData({
                        verified: true,
                        position: { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy },
                        message: 'GPS location captured (site coordinates unavailable)'
                      });
                    } catch (err) {
                      alert('Could not get your location. Please enable GPS and try again.');
                    }
                  }}
                >
                  Capture GPS Location
                </Button>
              </Box>
            ) : null}
            {locationVerified && (
              <Alert severity="success" icon={<CheckCircle />} sx={{ mt: 2 }}>
                Location verified! You may proceed to the next step.
              </Alert>
            )}
          </Box>
        );

      case 'Document with Photos':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step {stepNumber}: Document with Photos
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Take photos of the site, materials, and any issues you observe. AI will analyze them for defects.
            </Typography>
            <AIPhotoAnalyzer
              onPhotosChanged={handlePhotosChanged}
              initialPhotos={photos.length > 0 ? photos : initialPhotos}
            />
          </Box>
        );

      case 'Write Report':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step {stepNumber}: Write Inspection Report
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Document your findings. Use AI to generate a professional report from your notes.
            </Typography>
            <AIInspectionReportField
              inspectionType={verification?.verificationType || 'general'}
              value={reportContent}
              onChange={setReportContent}
              projectContext={{
                title: verification?.title,
                description: verification?.description,
                location: verification?.projectLocation,
                photoAnalysis: buildPhotoAnalysisContext()
              }}
              verificationRequest={{
                title: verification?.title,
                ownerRequest: verification?.description,
                focusAreas: verification?.focusAreas || []
              }}
            />
          </Box>
        );

      case 'Listing findings':
        if (listingTemplateError || listingFields.length === 0) {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step {stepNumber}: Listing findings
              </Typography>
              <Alert
                severity="error"
                action={<Button color="inherit" size="small" onClick={() => loadListingTemplate(verification?.packageSlug)}>Retry</Button>}
              >
                {LISTING_TEMPLATE_BLOCKED}
              </Alert>
            </Box>
          );
        }
        return (
          <ListingFindingsStep
            fields={listingFields}
            value={listingFindings}
            onChange={setListingFindings}
            showErrors={showFindingErrors}
            listingType={verification?.listingType}
          />
        );

      case 'Compliance Check':
        // Only show compliance check for Professional tier
        if (verifierInfo?.tier === 'professional') {
          return (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step {stepNumber}: Compliance Check
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Verify report compliance with building codes and project specifications.
              </Typography>
              <AIComplianceChecker
                report={reportContent}
                inspectionType={verification?.verificationType}
                onComplianceChecked={handleComplianceChecked}
              />
            </Box>
          );
        } else {
          // Skip compliance for General tier — handled by the effect that
          // advances past this Professional-only step. Render nothing meanwhile.
          return null;
        }

      case 'Review & Submit':
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Step {stepNumber}: Review & Submit
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Review all information before submitting your verification report.
            </Typography>

            <Grid container spacing={3}>
              {/* Location Summary */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Location
                  </Typography>
                  <Typography variant="body2">
                    {locationVerified ? '✓ Verified' : '✗ Not verified'}
                    {locationData && ` - ${locationData.distance?.toFixed(0)}m from site`}
                  </Typography>
                </Paper>
              </Grid>

              {/* Photos Summary */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Photos
                  </Typography>
                  <Typography variant="body2">
                    {photos.length} photo{photos.length !== 1 ? 's' : ''} uploaded
                  </Typography>
                </Paper>
              </Grid>

              {/* Report Summary */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Report
                  </Typography>
                  <Typography variant="body2">
                    {reportContent ? `${reportContent.length} characters` : 'No report content'}
                  </Typography>
                </Paper>
              </Grid>

              {/* Listing findings summary (listing verifications only) */}
              {isListingVerification && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Listing Findings
                    </Typography>
                    {listingFields.length === 0 ? (
                      <Typography variant="body2">Findings form unavailable</Typography>
                    ) : (
                      listingFields.map((f) => {
                        const v = listingFindings[f.id];
                        const shown = v === true ? 'Yes'
                          : v === false ? 'No'
                            : (v === undefined || v === null || v === '') ? '—'
                              : String(v).replace(/_/g, ' ');
                        return (
                          <Typography key={f.id} variant="body2">
                            <strong>{f.label}:</strong> {shown}
                          </Typography>
                        );
                      })
                    )}
                    {!isListingFindingsComplete(listingFields, listingFindings) && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        Some required findings are still missing — go back and complete them before submitting.
                      </Alert>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Compliance Summary (Professional only) */}
              {verifierInfo?.tier === 'professional' && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" color="primary" gutterBottom>
                      Compliance Check
                    </Typography>
                    <Typography variant="body2">
                      {complianceResult
                        ? `Status: ${complianceResult.overallStatus}`
                        : 'Not performed'}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              {/* Final report preview — the exact client view */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2, bgcolor: '#fff', textAlign: 'center' }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Final Report
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    See the report exactly as it will be delivered to your client before you submit.
                  </Typography>
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<Visibility />}
                    onClick={() => setShowClientPreview(true)}
                    disabled={!reportContent.trim()}
                  >
                    Preview Final Report (Client View)
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={() => navigate('/verifier/my-verifications')} sx={{ mt: 2 }}>
          Back to Verifications
        </Button>
      </Container>
    );
  }

  if (!verification) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Verification not found</Alert>
        <Button onClick={() => navigate('/verifier/my-verifications')} sx={{ mt: 2 }}>
          Back to Verifications
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          {verification.title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {verification.description}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Type: <strong>{verification.verificationType?.replace('_', ' ')}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Tier: <strong>{verification.tier}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cost: <strong>${verification.cost}</strong>
          </Typography>
        </Box>
      </Box>

      {/* Progress Stepper */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, minHeight: 20 }}>
          <Typography variant="caption" color="text.secondary">
            {draftSaving
              ? 'Saving…'
              : draftSavedAt
                ? `Saved ✓ ${draftSavedAt.toLocaleTimeString()}`
                : ''}
          </Typography>
        </Box>
      </Paper>

      {/* Step Content */}
      <Paper sx={{ p: 3, mb: 3, minHeight: 400 }}>
        {renderStepContent(activeStep)}
      </Paper>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          Back
        </Button>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              endIcon={<ArrowForward />}
              onClick={handleNext}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              startIcon={<Send />}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Verification'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Client-view preview of the final report */}
      <Dialog
        open={showClientPreview}
        onClose={() => setShowClientPreview(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => setShowClientPreview(false)} />
        <DialogContent sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#fafafa' }}>
          {showClientPreview && (
            <SimpleVerificationReport
              verification={buildPreviewVerification()}
              isClient={false}
              previewMode
            />
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default StructuredVerificationWorkflow;
